import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Trophy, TrendingUp, MapPin, Newspaper } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SectionHeading, FadeImg } from "@/components/Shared";
import EventCard from "@/components/EventCard";
import SponsorStrip from "@/components/SponsorStrip";
import { homeDefaults } from "@/pages/homeDefaults";
import { useSiteContent, mediaSrc } from "@/lib/siteContent";

const HERO = "https://images.unsplash.com/photo-1655159428752-c700435e9983?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [winners, setWinners] = useState([]);
  const [leader, setLeader] = useState(null);
  const [votm, setVotm] = useState(null);
  const content = useSiteContent();
  const home = { ...homeDefaults, ...(content?.home || {}) };

  useEffect(() => {
    api.get("/events?status=upcoming").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/winners").then((r) => setWinners(r.data.slice(0, 5))).catch(() => {});
    api.get("/leaderboards/leader").then((r) => setLeader(r.data)).catch(() => {});
    api.get("/venue-of-the-month").then((r) => setVotm(r.data)).catch(() => {});
  }, []);

  if (!content) return <div className="min-h-[88vh]" data-testid="home-page-loading" />;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <FadeImg src={mediaSrc(home.hero_image) || HERO} alt="Poker" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <div className="text-gold text-xs uppercase tracking-[0.4em] mb-5">{home.hero_eyebrow}</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
              {home.hero_title_line1}<br /><span className="gold-text">{home.hero_title_line2}</span>
            </h1>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-xl">
              {home.hero_subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/events" data-testid="hero-events-btn">
                <Button className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest px-8 py-6 drop-shadow-[0_0_15px_rgba(212,175,55,0.3)]">
                  Upcoming Events <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/become-a-legend" data-testid="hero-join-btn">
                <Button variant="outline" className="border-2 border-gold text-gold hover:bg-gold hover:text-navy rounded-none font-bold uppercase tracking-widest px-8 py-6 bg-transparent">
                  Become a Legend
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-white/10 bg-navy-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {home.stats.map((s, i) => (
            <div key={s.l || i} className="py-10 px-4 text-center">
              <div className="font-display text-3xl md:text-4xl font-bold text-gold">{s.n}</div>
              <div className="text-slate-400 text-xs uppercase tracking-widest mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* REIGNING LEADER */}
      {leader && leader.name && (
        <section className="border-b border-white/10 bg-gradient-to-r from-gold/10 via-navy to-navy overflow-hidden" data-testid="reigning-leader">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col sm:flex-row items-center gap-8">
            <div className="w-28 h-28 border-2 border-gold overflow-hidden bg-navy-paper flex items-center justify-center flex-shrink-0 drop-shadow-[0_0_25px_rgba(212,175,55,0.25)]">
              {leader.picture ? (
                <img src={leader.picture.startsWith("http") ? leader.picture : `${process.env.REACT_APP_BACKEND_URL}${leader.picture}`} alt={leader.name} className="w-full h-full object-cover" />
              ) : (
                <Trophy className="w-12 h-12 text-gold" />
              )}
            </div>
            <div className="text-center sm:text-left flex-1">
              <div className="text-gold text-xs uppercase tracking-[0.4em] mb-2">Reigning Season Leader</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white">{leader.name}</h2>
              <p className="text-slate-300 mt-2">
                <span className="text-gold font-bold">{leader.points} points</span> · {leader.wins} win{leader.wins === 1 ? "" : "s"} · {leader.events_played} events · {leader.season}
              </p>
            </div>
            <Link to={`/players/${encodeURIComponent(leader.name)}`} data-testid="leader-profile-link">
              <Button className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest px-7 py-5">View Profile</Button>
            </Link>
          </div>
        </section>
      )}

      {/* UPCOMING EVENTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12">
          <SectionHeading eyebrow="What's On" title="Upcoming Events" />
          <Link to="/events" className="hidden md:flex items-center gap-2 text-gold hover:gap-3 transition-all text-sm uppercase tracking-widest" data-testid="all-events-link">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {events.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
        </div>
      </section>

      {/* VENUE OF THE MONTH */}
      {votm && votm.venue && (
        <section className="border-b border-white/10" data-testid="venue-of-the-month">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative h-72 lg:h-80 overflow-hidden border border-white/10">
              {votm.image ? <img src={votm.image} alt={votm.venue} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-navy-paper flex items-center justify-center"><MapPin className="w-12 h-12 text-gold" /></div>}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
              <span className="absolute top-4 left-4 bg-gold text-navy text-[10px] font-bold uppercase tracking-widest px-3 py-1">Venue of the Month</span>
            </div>
            <div>
              <div className="text-gold text-xs uppercase tracking-[0.4em] mb-3">Busiest Room</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white">{votm.venue}</h2>
              <p className="text-gold text-sm mt-2 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {votm.city}</p>
              <p className="text-slate-300 mt-4 leading-relaxed">{votm.description}</p>
              <div className="flex gap-8 mt-6">
                <div><div className="font-display text-3xl font-bold text-gold">{votm.players}</div><div className="text-slate-400 text-xs uppercase tracking-widest mt-1">Players</div></div>
                <div><div className="font-display text-3xl font-bold text-gold">{votm.events}</div><div className="text-slate-400 text-xs uppercase tracking-widest mt-1">Results Logged</div></div>
              </div>
              <Link to="/where-to-play" className="inline-flex items-center gap-2 text-gold hover:gap-3 transition-all text-sm uppercase tracking-widest mt-6" data-testid="votm-link">
                Where to Play <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* NEWS + WINNERS */}
      <section className="bg-navy-paper border-y border-white/10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
          <div>
            <SectionHeading eyebrow="Latest" title="News & Announcements" className="mb-8" />
            <div className="space-y-5">
              {news.map((n, i) => (
                <div key={n.id} className="flex gap-4 group animate-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`news-item-${n.id}`}>
                  <img src={n.image} alt="" className="w-24 h-24 object-cover flex-shrink-0 border border-white/10" />
                  <div>
                    <div className="text-gold text-xs uppercase tracking-widest flex items-center gap-1.5"><Newspaper className="w-3 h-3" /> {n.date}</div>
                    <h4 className="font-display text-lg font-bold text-white mt-1 group-hover:text-gold transition-colors">{n.title}</h4>
                    <p className="text-slate-400 text-sm mt-1 line-clamp-2">{n.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading eyebrow="Hall of Fame" title="Recent Winners" className="mb-8" />
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
              {winners.map((w, i) => (
                <div key={w.id} className="group animate-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`winner-${w.id}`}>
                  <div className="relative overflow-hidden border border-white/10 aspect-[3/4]">
                    <img src={w.photo} alt={w.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent" />
                    <Trophy className="absolute top-2 right-2 w-4 h-4 text-gold" />
                  </div>
                  <div className="mt-2">
                    <div className="text-white font-semibold text-sm">{w.name}</div>
                    <div className="text-gold text-xs">{w.prize}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/gallery" className="inline-flex items-center gap-2 text-gold hover:gap-3 transition-all text-sm uppercase tracking-widest mt-6" data-testid="gallery-link">
              View Gallery <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: TrendingUp, to: "/leaderboards" },
            { icon: MapPin, to: "/where-to-play" },
            { icon: Trophy, to: "/points-payouts" },
          ].map((c, i) => (
            <Link key={c.to} to={c.to} className="group bg-navy-paper border border-slate-800 hover:border-gold/50 p-8 transition-all duration-300 hover:-translate-y-1" data-testid={`cta-card-${i}`}>
              <c.icon className="w-9 h-9 text-gold mb-5" />
              <h3 className="font-display text-xl font-bold text-white group-hover:text-gold transition-colors">          {home.cta[i]?.title}</h3>
              <p className="text-slate-400 text-sm mt-2">{home.cta[i]?.desc}</p>
            </Link>
          ))}
        </div>
        <SponsorStrip variant="home" testId="home-sponsor-strip" />
      </section>
    </div>
  );
}
